import React, { useEffect, useState, useRef } from "react";
import career_image from "../assets/images/NSS/main.webp";
import internImg from "../assets/images/NSS/NSPs.webp";
import { fetchFromStrapi } from "../api/strapi";

const STRAPI_HOST = "https://ecl-cms-5d0d78c9e9b5.herokuapp.com";
// import { postToStrapi, uploadFile } from "../api/strapi";

const EMPTY_FORM = {
  name: "",
  email: "",
  videoLink: "",
  file: null,
  coverLetter: null,
  certificates: [],
  selfImprovement: "",
  selfLearning: "",
  problemSolving: "",
  commitment: "",
  achievement: "",
};

const TOTAL_STEPS = 4;
const MIN_CHARS = 100;

export default function Careers() {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState(EMPTY_FORM);

  const fileInputRef = useRef(null);
  const coverLetterRef = useRef(null);
  const certificatesRef = useRef(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const res = await fetchFromStrapi("/ecl-career-sections");
        const careerList = res?.data?.data || res?.data || [];
        setCareers(careerList);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const openModal = (role, category) => {
    setSelectedRole(role);
    setSelectedCategory(category || null);
    setCurrentStep(1);
    setFormData(EMPTY_FORM);
    setError("");
    setSuccess(false);
    setShowModal(true);
  };

  const openModal2 = role => {
    setSelectedRole(role);
    setCurrentStep(1);
    setFormData(EMPTY_FORM);
    setError("");
    setSuccess(false);
    setShowModal2(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData(EMPTY_FORM);
    setCurrentStep(1);
    setError("");
    setSuccess(false);
  };

  const closeModal2 = () => {
    setShowModal2(false);
    setFormData(EMPTY_FORM);
    setCurrentStep(1);
    setError("");
    setSuccess(false);
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  const handleFileChange = file => {
    if (!file) return;
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedTypes.includes(file.type)) {
      setError("Only PDF or DOCX files are allowed.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("File size must be less than 5MB.");
      return;
    }
    setError("");
    setFormData(prev => ({ ...prev, file }));
  };

  const handleDrop = e => {
    e.preventDefault();
    handleFileChange(e.dataTransfer.files[0]);
  };

  const handleCoverLetterChange = file => {
    if (!file) return;
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedTypes.includes(file.type)) { setError("Only PDF or DOCX files are allowed."); return; }
    if (file.size > MAX_FILE_SIZE) { setError("File size must be less than 5MB."); return; }
    setError("");
    setFormData(prev => ({ ...prev, coverLetter: file }));
  };

  const handleCertificatesChange = files => {
    const arr = Array.from(files);
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    for (const f of arr) {
      if (!allowedTypes.includes(f.type)) { setError("Only PDF or DOCX files are allowed."); return; }
      if (f.size > MAX_FILE_SIZE) { setError("File size must be less than 5MB."); return; }
    }
    setError("");
    setFormData(prev => ({ ...prev, certificates: [...prev.certificates, ...arr] }));
  };

  const removeCertificate = idx =>
    setFormData(prev => ({ ...prev, certificates: prev.certificates.filter((_, i) => i !== idx) }));

  const setField = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  // Certificates are optional for NSS category career entries
  const categoryLower = (selectedCategory || "").trim().toLowerCase();
  const isInternCategory = categoryLower.includes("intern");
  const certificatesOptional = categoryLower === "nss" || isInternCategory;
  // Interns only need to submit a CV — Cover Letter and Certificates are optional for them
  const coverLetterOptional = isInternCategory;

  const validateStep = step => {
    if (step === 1) {
      if (!formData.name.trim()) return "Please enter your full name.";
      if (!formData.email.trim()) return "Please enter your email address.";
      if (!formData.file) return "Please upload your CV before continuing.";
      if (!coverLetterOptional && !formData.coverLetter) return "Please upload your Cover Letter before continuing.";
      if (!certificatesOptional && !formData.certificates.length) return "Please upload your Certificates before continuing.";
    }
    if (step === 2) {
      if (formData.selfImprovement.trim().length < MIN_CHARS)
        return `Please write at least ${MIN_CHARS} characters for the first question.`;
      if (formData.selfLearning.trim().length < MIN_CHARS)
        return `Please write at least ${MIN_CHARS} characters for the second question.`;
    }
    if (step === 3) {
      if (formData.problemSolving.trim().length < MIN_CHARS)
        return `Please write at least ${MIN_CHARS} characters for the first question.`;
      if (formData.commitment.trim().length < MIN_CHARS)
        return `Please write at least ${MIN_CHARS} characters for the second question.`;
    }
    if (step === 4) {
      if (formData.achievement.trim().length < MIN_CHARS)
        return `Please write at least ${MIN_CHARS} characters for this question.`;
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep(currentStep);
    if (err) { setError(err); return; }
    setError("");
    setCurrentStep(s => s + 1);
  };

  const handleBack = () => {
    setError("");
    setCurrentStep(s => s - 1);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const err = validateStep(4);
    if (err) { setError(err); return; }

    try {
      setSubmitting(true);
      setError("");

      const toBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const cvBase64 = await toBase64(formData.file);
      const coverLetterBase64 = formData.coverLetter ? await toBase64(formData.coverLetter) : null;
      const certificatesData = await Promise.all(
        formData.certificates.map(async f => ({
          base64: await toBase64(f),
          fileName: f.name,
          mimeType: f.type,
        }))
      );

      // // Strapi submission (commented out — now handled via email)
      // const uploadedFiles = await uploadFile(formData.file);
      // if (!uploadedFiles || !uploadedFiles.length) throw new Error("File upload failed.");
      // const fileId = uploadedFiles[0].id;
      // await postToStrapi("/ecl-career-applications", {
      //   data: {
      //     name: formData.name,
      //     email: formData.email,
      //     position: selectedRole,
      //     cv: fileId,
      //   },
      // });

      const res = await fetch("/.netlify/functions/send-application", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          videoLink: formData.videoLink,
          position: selectedRole,
          cvBase64,
          cvFileName: formData.file.name,
          cvMimeType: formData.file.type,
          coverLetterBase64,
          coverLetterFileName: formData.coverLetter?.name || null,
          coverLetterMimeType: formData.coverLetter?.type || null,
          certificates: certificatesData,
          selfImprovement: formData.selfImprovement,
          selfLearning: formData.selfLearning,
          problemSolving: formData.problemSolving,
          commitment: formData.commitment,
          achievement: formData.achievement,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to send application");
      }

      setSuccess(true);
      setTimeout(() => closeModal(), 3000);
    } catch (err) {
      console.error("Submission failed:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Step content ───────────────────────────────────────────── */
  const stepTitles = [
    "Your Details",
    "Growth & Learning",
    "Problem Solving",
    "Achievement",
  ];

  function CharCount({ value, min = MIN_CHARS }) {
    const count = value.trim().length;
    const color = count >= min ? "#16a34a" : "#9CA3AF";
    return (
      <small style={{ color, float: "right" }}>{count}/{min} min</small>
    );
  }

  function StepOne() {
    return (
      <>
        <div className="mb-3">
          <label className="form-label fw-semibold small">Full Name *</label>
          <input
            type="text"
            className="form-control"
            required
            value={formData.name}
            onChange={e => setField("name", e.target.value)}
            placeholder="Kwame Asante"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold small">Email *</label>
          <input
            type="email"
            className="form-control"
            required
            value={formData.email}
            onChange={e => setField("email", e.target.value)}
            placeholder="kwame@company.com"
          />
        </div>

        {/* <div className="mb-3">
          <label className="form-label fw-semibold small">
            Video Link{" "}
            <span className="text-muted fw-normal" style={{ fontSize: "0.78rem" }}>
              (Upload the link to a 1-minute video introducing yourself )
            </span>
          </label>
          <input
            type="url"
            className="form-control"
            value={formData.videoLink}
            onChange={e => setField("videoLink", e.target.value)}
            placeholder="https://..."
          />
        </div> */}

        <div className="mb-1">
          <label className="form-label fw-semibold small">CV / Resume *</label>
        </div>
        <div
          className="border rounded-3 p-4 text-center"
          style={{ borderStyle: "dashed", cursor: "pointer", borderColor: formData.file ? "#16a34a" : "#d1d5db" }}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current.click()}
        >
          {formData.file ? (
            <p className="mb-0 fw-semibold" style={{ color: "#16a34a" }}>
              ✓ {formData.file.name}
            </p>
          ) : (
            <>
              <p className="mb-1 fw-semibold" style={{ color: "#374151" }}>Drag & drop your CV here</p>
              <small className="text-muted">or click to browse · PDF or DOCX · max 5MB</small>
            </>
          )}
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            ref={fileInputRef}
            hidden
            onChange={e => handleFileChange(e.target.files[0])}
          />
        </div>

        <div className="mb-1 mt-3">
          <label className="form-label fw-semibold small">
            Cover Letter{coverLetterOptional ? "" : " *"}
            {coverLetterOptional && (
              <span className="text-muted fw-normal" style={{ fontSize: "0.78rem" }}> (optional)</span>
            )}
          </label>
        </div>
        <div
          className="border rounded-3 p-4 text-center"
          style={{ borderStyle: "dashed", cursor: "pointer", borderColor: formData.coverLetter ? "#16a34a" : "#d1d5db" }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleCoverLetterChange(e.dataTransfer.files[0]); }}
          onClick={() => coverLetterRef.current.click()}
        >
          {formData.coverLetter ? (
            <p className="mb-0 fw-semibold" style={{ color: "#16a34a" }}>✓ {formData.coverLetter.name}</p>
          ) : (
            <>
              <p className="mb-1 fw-semibold" style={{ color: "#374151" }}>Drag & drop your cover letter here</p>
              <small className="text-muted">or click to browse · PDF or DOCX · max 5MB</small>
            </>
          )}
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            ref={coverLetterRef}
            hidden
            onChange={e => handleCoverLetterChange(e.target.files[0])}
          />
        </div>

        <div className="mb-1 mt-3">
          <label className="form-label fw-semibold small">
            Certificates{certificatesOptional ? "" : " *"}
            <span className="text-muted fw-normal" style={{ fontSize: "0.78rem" }}>
              {certificatesOptional ? "(optional · multiple allowed)" : "(multiple allowed)"}
            </span>
          </label>
        </div>
        <div
          className="border rounded-3 p-4 text-center"
          style={{ borderStyle: "dashed", cursor: "pointer", borderColor: formData.certificates.length ? "#16a34a" : "#d1d5db" }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleCertificatesChange(e.dataTransfer.files); }}
          onClick={() => certificatesRef.current.click()}
        >
          <p className="mb-1 fw-semibold" style={{ color: "#374151" }}>Drag & drop certificates here</p>
          <small className="text-muted">or click to browse · PDF or DOCX · max 5MB each</small>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            ref={certificatesRef}
            hidden
            multiple
            onChange={e => handleCertificatesChange(e.target.files)}
          />
        </div>
        {formData.certificates.length > 0 && (
          <ul className="mt-2 mb-0 ps-0" style={{ listStyle: "none" }}>
            {formData.certificates.map((f, i) => (
              <li key={i} className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: "0.82rem" }}>
                <span style={{ color: "#16a34a" }}>✓</span>
                <span style={{ color: "#374151" }}>{f.name}</span>
                <button
                  type="button"
                  className="btn btn-link p-0 ms-1"
                  style={{ fontSize: "0.75rem", color: "#dc2626", textDecoration: "none" }}
                  onClick={() => removeCertificate(i)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }

  function StepTwo() {
    return (
      <>
        <div className="mb-4">
          <label className="form-label fw-semibold small">
            What's an area where you have actively worked to improve yourself? How did you go about it?
            {CharCount({ value: formData.selfImprovement })}
          </label>
          <textarea
            className="form-control"
            rows={4}
            style={{ resize: "none" }}
            value={formData.selfImprovement}
            onChange={e => setField("selfImprovement", e.target.value)}
            placeholder="Describe a specific area of growth and the steps you took..."
          />
        </div>

        <div className="mb-2">
          <label className="form-label fw-semibold small">
            What have you learned on your own (outside of formal education) in the last 6 months?
            {CharCount({ value: formData.selfLearning })}
          </label>
          <textarea
            className="form-control"
            rows={4}
            style={{ resize: "none" }}
            value={formData.selfLearning}
            onChange={e => setField("selfLearning", e.target.value)}
            placeholder="Books, courses, skills, projects you pursued independently..."
          />
        </div>
      </>
    );
  }

  function StepThree() {
    return (
      <>
        <div className="mb-4">
          <label className="form-label fw-semibold small">
            Describe a situation where the obvious solution wouldn't work. What did you do?
            {CharCount({ value: formData.problemSolving })}
          </label>
          <textarea
            className="form-control"
            rows={4}
            style={{ resize: "none" }}
            value={formData.problemSolving}
            onChange={e => setField("problemSolving", e.target.value)}
            placeholder="Walk us through the situation and your alternative approach..."
          />
        </div>

        <div className="mb-2">
          <label className="form-label fw-semibold small">
            Tell us about something you committed to that didn't excite you initially. How did you handle it?
            {CharCount({ value: formData.commitment })}
          </label>
          <textarea
            className="form-control"
            rows={4}
            style={{ resize: "none" }}
            value={formData.commitment}
            onChange={e => setField("commitment", e.target.value)}
            placeholder="What was the commitment, and what kept you going..."
          />
        </div>
      </>
    );
  }

  function StepFour() {
    return (
      <div className="mb-2">
        <label className="form-label fw-semibold small">
          Tell us about something you built, led, or accomplished that you're proud of.
          {CharCount({ value: formData.achievement })}
        </label>
        <textarea
          className="form-control"
          rows={6}
          style={{ resize: "none" }}
          value={formData.achievement}
          onChange={e => setField("achievement", e.target.value)}
          placeholder="What did you do, what was your role, and why does it matter to you..."
        />
      </div>
    );
  }

  /* ── Progress bar ───────────────────────────────────────────── */
  function StepProgress() {
    return (
      <div className="px-4 pt-3 pb-1">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span style={{ fontSize: "0.75rem", color: "#6B7280", fontWeight: 500 }}>
            Step {currentStep} of {TOTAL_STEPS} — {stepTitles[currentStep - 1]}
          </span>
          <span style={{ fontSize: "0.75rem", color: "#6B7280" }}>
            {Math.round((currentStep / TOTAL_STEPS) * 100)}%
          </span>
        </div>
        <div style={{ height: 4, background: "#e5e7eb", borderRadius: 99 }}>
          <div
            style={{
              height: "100%",
              width: `${(currentStep / TOTAL_STEPS) * 100}%`,
              background: "#0a1a3a",
              borderRadius: 99,
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="text-dark">
      {/* Hero */}
      <section
        style={{
          position: "relative",
          height: 520,
          backgroundImage: `url(${career_image})`,
          backgroundSize: "cover",
          backgroundPosition: "top center",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="hero-info-card">
            <p className="text-uppercase fw-semibold mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}>
              Join our team
            </p>
            <h1 className="fw-bold mb-3" style={{ fontSize: "clamp(2.1rem, 4vw, 3.2rem)", lineHeight: 1.15, color: "#0a1a3a" }}>
              Careers at ECL
            </h1>
            <p className="mb-0" style={{ color: "#4B5563", lineHeight: 1.7 }}>
              We are more than a Tech Company; we are a community of dedicated individuals committed to driving excellence in the business world.
            </p>
          </div>
        </div>
      </section>

      {/* Available Roles */}
      <section className="py-5" style={{ background: "#f8fafc" }}>
        <div className="container">
          <div className="mb-5">
            <p className="text-uppercase fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}>
              Open positions
            </p>
            <h2 className="fw-bold" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", color: "#0a1a3a" }}>
              Available Roles
            </h2>
          </div>

          {loading ? (
            <div className="d-flex flex-column gap-3">
              {[1, 2, 3].map(n => (
                <div key={n} className="bg-white rounded-3 p-4" style={{ height: 88, opacity: 0.5, border: "1px solid #e5e7eb" }} />
              ))}
            </div>
          ) : careers.length === 0 ? (
            <p style={{ color: "#6B7280" }}>No open roles at this time. Check back soon.</p>
          ) : (
            <div className="d-flex flex-column gap-3">
              {careers.map((item, i) => {
                const rawUrl = item.attributes.file?.data?.[0].attributes?.url;
                const fileHref = typeof rawUrl === "string"
                  ? (rawUrl.startsWith("http") ? rawUrl : `${STRAPI_HOST}${rawUrl}`)
                  : null;
                return (
                  <div
                    key={i}
                    className="d-flex justify-content-between align-items-center bg-white rounded-3 p-4"
                    style={{ border: "1px solid #e5e7eb", transition: "box-shadow 0.2s ease, transform 0.2s ease" }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                  >
                    <div>
                      <h5 className="mb-1" style={{ fontWeight: 600, color: "#0a1a3a", fontSize: "1rem" }}>
                        {item.attributes.Title}
                      </h5>
                      <p className="mb-2" style={{ color: "#6B7280", fontSize: "0.875rem", margin: 0 }}>
                        {item.attributes.Description}
                      </p>
                      <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                        {item.attributes.Tag && (
                          <span style={{ display: "inline-block", padding: "2px 10px", fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", borderRadius: 20, background: "#EFF6FF", color: "#1D4ED8" }}>
                            {item.attributes.Tag}
                          </span>
                        )}
                        {fileHref && (
                          <a
                            href={fileHref}
                            target="_blank"
                            rel="noreferrer"
                            download
                            style={{ fontSize: "0.78rem", color: "#009EDC", fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid #e5e7eb", padding: "4px 12px", borderRadius: 6, background: "#f8fafc"  }}
                            onClick={e => e.stopPropagation()}
                          >
                             View the full Job Description
                          </a>
                        )}
                      </div>
                    </div>
                    <button
                      className="btn btn-dark flex-shrink-0 ms-4"
                      style={{ borderRadius: 6, padding: "8px 20px", fontSize: "0.875rem" }}
                      onClick={() => openModal(item.attributes.Title, item.attributes.Category)}
                    >
                      Apply Now
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxHeight: "90vh" }}>
            <div className="modal-content rounded-4" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>

              {/* Header */}
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold mb-0">{selectedRole}</h5>
                  <small className="text-muted">Application</small>
                </div>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>

              {success ? (
                <div className="text-center p-5">
                  <div
                    className="mx-auto mb-4"
                    style={{ width: 80, height: 80, borderRadius: "50%", backgroundColor: "#198754", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <span style={{ color: "white", fontSize: 36, fontWeight: "bold" }}>✓</span>
                  </div>
                  <h5 className="fw-bold">Application Submitted!</h5>
                  <p className="text-muted mb-0">Our team will review your application and be in touch shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}>
                  <StepProgress />

                  {/* Scrollable body */}
                  <div className="modal-body pt-3" style={{ overflowY: "auto", flex: 1 }}>
                    {currentStep === 1 && StepOne()}
                    {currentStep === 2 && StepTwo()}
                    {currentStep === 3 && StepThree()}
                    {currentStep === 4 && StepFour()}

                    {error && (
                      <div className="text-danger mt-3 small">{error}</div>
                    )}
                  </div>

                  {/* Footer nav */}
                  <div className="modal-footer border-0 pt-2">
                    {currentStep === 1 ? (
                      <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                    ) : (
                      <button type="button" className="btn btn-light" onClick={handleBack}>Back</button>
                    )}

                    {currentStep < TOTAL_STEPS ? (
                      <button type="button" className="btn btn-dark" onClick={handleNext}>
                        Next →
                      </button>
                    ) : (
                      <button type="submit" className="btn btn-dark" disabled={submitting}>
                        {submitting ? "Submitting…" : "Submit Application"}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NSP Section */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-md-6">
              <div className="overflow-hidden" style={{ borderRadius: 12, boxShadow: "0 16px 48px rgba(0,0,0,0.12)" }}>
                <img src={internImg} alt="NSP interns at ECL" className="img-fluid w-100" style={{ display: "block" }} />
              </div>
            </div>
            <div className="col-md-6">
              <p className="text-uppercase fw-semibold mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}>
                National Service
              </p>
              <h2 className="fw-bold mb-3" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", color: "#0a1a3a" }}>
                NSPs at ECL
              </h2>
              <p style={{ color: "#4B5563", lineHeight: 1.8 }}>
                We are more than a Tech Company; we are a community of dedicated individuals committed to driving excellence in the business world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Notice */}
      <section className="py-5" style={{ background: "#0a1a3a" }}>
        <div className="container">
          <div className="rounded-3 p-4 p-md-5" style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }}>
            <p className="text-uppercase fw-semibold mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}>
              Important notice
            </p>
            <h3 className="fw-bold mb-3" style={{ color: "#0a1a3a" }}>Careers at ECL</h3>
            <p style={{ color: "#4B5563", lineHeight: 1.8, fontSize: "0.95rem", marginBottom: 0 }}>
              At ECL, we prioritize the security and integrity of our job application process.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
